'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  PasswordInput,
} from '@workspace/ui';
import { getPostHogDistinctId, identifyPostHogUser } from '@workspace/analytics/posthog/client';
import {
  isInternalEmail,
  optInAnalyticsInBrowser,
  optOutAnalyticsInBrowser,
} from '@workspace/analytics/posthog/opt-out';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { login } from '@/actions/mixed/auth';
import { DontHaveAnAccountYet } from '@/components/auth/dont-have-an-account-yet';
import { buildAppRedirectUrl } from '@/lib/get-redirect-url';

const loginSchema = z.object({
  email: z.string().trim().email({ message: 'Please enter a valid email.' }),
  password: z
    .string()
    .trim()
    .min(1, { message: 'Password is required' })
    .min(6, { message: 'Password must be at least 6 characters.' }),
});

export default function LoginForm({
  redirectToPage,
}: {
  redirectToPage: string | null | undefined;
}) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onTouched',
  });

  async function onSubmit(values: z.infer<typeof loginSchema>) {
    const email = values.email.trim();
    const password = values.password.trim();
    const isInternal = isInternalEmail(email);

    if (isInternal) optOutAnalyticsInBrowser();
    else await optInAnalyticsInBrowser();

    const analyticsDistinctId = isInternal ? undefined : getPostHogDistinctId();

    try {
      setIsLoading(true);

      const result = await login(email, password, analyticsDistinctId);

      if (result.requires2FA) {
        router.replace('/two-factor-auth');

        return;
      }

      const feAppUrl = result.fe_app_url;

      if (!feAppUrl) {
        throw new Error('Invalid response from server');
      }

      if (!isInternal && result.userId) {
        await identifyPostHogUser({
          app: 'auth-app',
          id: result.userId,
          email,
          roles: result.roles,
          teams: result.teams,
          organisationId: result.organisationId,
        });
      }

      window.location.href = buildAppRedirectUrl(feAppUrl, redirectToPage ?? undefined);
    } catch (err) {
      if (err instanceof Error && 'statusCode' in err && err.statusCode === 403) {
        toast.error('This user does not have permission to access this application');
        setIsLoading(false);
        return;
      }

      const errorMessage = err instanceof Error ? err.message : 'Failed to login';
      toast.error(errorMessage);

      setIsLoading(false);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="justify-start text-sm leading-none font-medium text-neutral-200">
                Email
              </FormLabel>
              <FormControl>
                <Input
                  autoComplete="email"
                  type="email"
                  aria-invalid={form.formState.errors.email ? true : undefined}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="justify-start text-sm leading-none font-medium text-neutral-200">
                Password
              </FormLabel>
              <FormControl>
                <PasswordInput
                  autoComplete="current-password"
                  aria-invalid={form.formState.errors.password ? true : undefined}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex flex-col gap-7">
          <Link
            href="/forgot-password"
            className="text-grey-900 ml-auto w-fit text-right text-sm leading-none font-medium dark:text-neutral-100"
          >
            Forgot password?
          </Link>

          <div className="flex flex-col gap-7">
            <Button
              className="gap-6"
              type="submit"
              isLoading={isLoading}
              loadingText={'Signing in'}
            >
              {'Login'}
            </Button>

            {process.env.ENVIRONMENT_NAME !== 'prod' && <DontHaveAnAccountYet />}
          </div>
        </div>
      </form>
    </Form>
  );
}
